import { Injectable } from '@angular/core';
import { Client } from '@app/core/entity/client';
import { State } from '@app/core/entity/state';
import { CloudCompanyService } from '@app/core/services/cloud-company.service';
import { OnboardingService } from '@app/core/services/onboarding.service';
import { ToastActions } from '@app/core/state/toast/toast.actions';
import { openOnboardingInNewTab } from '@app/shared/misc/openBlappOnboarding';
import { openTranslatableDialog } from '@app/shared/misc/openDialog';
import { Store } from '@ngrx/store';
import { DialogService } from 'primeng/dynamicdialog';
import { EMPTY, Observable, catchError, map, mergeMap, of, tap, throwError } from 'rxjs';
import { TranslatableService } from 'src/app/core/i18n/TranslatableService';
import { ExportWizardDialogComponent, WizardResponse } from './export-wizard-dialog.component';

@Injectable()
export class CreateCloudDbWizardService extends TranslatableService {
  // eslint-disable-next-line max-params
  constructor(
    private store: Store<State>,
    private dialogService: DialogService,
    private onboardingService: OnboardingService,
    private cloudCompanyService: CloudCompanyService,
  ) {
    super('CreateCloudDbWizardService');
  }

  runOnboardingWizard(client: Client): Observable<void> {
    return this.showExportWizardForOnboarding(client);
  }

  runCreateCloudDbWizard(client: Client): Observable<string> {
    return this.showExportWizardForCreateCloudDB(client);
  }

  private showExportWizardForOnboarding(client: Client): Observable<void> {
    return this.getDialogObservable(client).pipe(
      mergeMap((result) => this.generateBlappOnboardingKeyForClient(client, result)),
      tap((key) => openOnboardingInNewTab(key, this.translationService.getActiveLang())),
      mergeMap(() => EMPTY),
    );
  }

  private showExportWizardForCreateCloudDB(client: Client): Observable<string | null> {
    return this.getDialogObservable(client).pipe(
      mergeMap((result) => this.cloudCompanyService.exportClientToCloud(result)),
      map((result) => result.cloudApiKey),
      tap(() => this.dispatchCreateCloudDbSuccess()),
      catchError((err: unknown) => {
        this.dispatchCreateCloudDbError();
        return throwError(() => err);
      }),
    );
  }

  private getDialogObservable(client: Client): Observable<WizardResponse> {
    return openTranslatableDialog({
      deps: { dialogService: this.dialogService, translationService: this.translationService, store: this.store },
      componentType: ExportWizardDialogComponent,
      data: { client },
    }).pipe(
      mergeMap((result) => (result ? of(result) : EMPTY)), // if result is null (cancel was clicked), return empty observable
    );
  }

  private generateBlappOnboardingKeyForClient(client: Client, result: WizardResponse): Observable<string> {
    const { accountingMethod, financialYear, vatAccountingInterval } = result;
    const financialInformation = { accountingMethod, financialYear, vatAccountingInterval };
    return this.onboardingService.getOnboardingKey(client.id, { financialInformation });
  }

  private dispatchCreateCloudDbSuccess() {
    this.store.dispatch(
      ToastActions.showInfoMessage({
        summary: this.translate('createSuccessHead'),
        detail: this.translate('createSuccessText'),
      }),
    );
  }

  private dispatchCreateCloudDbError() {
    this.store.dispatch(
      ToastActions.showErrorMessage({
        summary: this.translate('errorOccured'),
        detail: this.translate('createErrorText'),
      }),
    );
  }
}
